import { useState, useCallback, useRef } from 'react';
import {
  Upload,
  Plus,
  Play,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  FileText,
  Video as VideoIcon,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useVideoSources } from './hooks/useVideoSources';
import { LocationFilter, LocationFilterValue } from './components/LocationFilter';
import { VideoItem, PROGRAM_OPTIONS, EVENT_OPTIONS, ASSET_TYPE_OPTIONS } from './types/video';

export const VideoSources = () => {
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualLocation, setManualLocation] = useState<LocationFilterValue>({});
  const [manualIconikUrl, setManualIconikUrl] = useState('');
  const [selectedVideos, setSelectedVideos] = useState<Set<number>>(new Set());
  const [editingVideo, setEditingVideo] = useState<{ index: number; video: VideoItem } | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', program: '', asset_type: 'Video', event: '' });
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const {
    videos,
    loading,
    error,
    addFromCSV,
    addManual,
    removeVideo,
    previewVideos,
    updateMetadata,
    uploadVideos,
    clearAll
  } = useVideoSources();

  // Handle CSV file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await addFromCSV(file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [addFromCSV]);

  // Handle manual URL addition
  const handleManualAdd = useCallback(() => {
    if (manualLocation.village_id && manualIconikUrl) {
      addManual(manualLocation.village_id, manualIconikUrl);
      setManualLocation({});
      setManualIconikUrl('');
      setShowManualInput(false);
    }
  }, [manualLocation, manualIconikUrl, addManual]);

  // Toggle video selection
  const toggleVideoSelection = useCallback((index: number) => {
    setSelectedVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Select all videos
  const selectAll = useCallback(() => {
    setSelectedVideos(new Set(videos.map((_, i) => i)));
  }, [videos]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedVideos(new Set());
  }, []);

  // Bulk actions
  const handleBulkPreview = useCallback(() => {
    const indices = Array.from(selectedVideos);
    previewVideos(indices);
  }, [selectedVideos, previewVideos]);

  const handleBulkUpload = useCallback(() => {
    const indices = Array.from(selectedVideos);
    const selectedVideoItems = indices.map(i => videos[i]);
    
    // Check if any selected videos are missing required fields
    const invalidVideos = selectedVideoItems.filter(video => 
      video.status === 'ready' && (!video.metadata.title || !video.metadata.program)
    );
    
    if (invalidVideos.length > 0) {
      setValidationError(`${invalidVideos.length} videos are missing required fields (title, program). Please edit their metadata first.`);
      return;
    }
    
    uploadVideos(indices);
  }, [selectedVideos, uploadVideos, videos]);

  const handleBulkDelete = useCallback(() => {
    const indices = Array.from(selectedVideos).sort((a, b) => b - a);
    indices.forEach(index => removeVideo(index));
    setSelectedVideos(new Set());
  }, [selectedVideos, removeVideo]);

  // Video playback
  const handleVideoPlay = useCallback((index: number) => {
    setPlayingVideo(playingVideo === index ? null : index);
  }, [playingVideo]);

  // Get status color and icon
  const getStatusDisplay = (status: VideoItem['status']) => {
    switch (status) {
      case 'pending':
        return { color: 'text-gray-500', icon: Clock, text: 'Pending' };
      case 'previewing':
        return { color: 'text-blue-500', icon: RefreshCw, text: 'Previewing' };
      case 'ready':
        return { color: 'text-green-500', icon: CheckCircle, text: 'Ready' };
      case 'uploading':
        return { color: 'text-blue-500', icon: RefreshCw, text: 'Uploading' };
      case 'uploaded':
        return { color: 'text-green-600', icon: CheckCircle, text: 'Uploaded' };
      case 'error':
        return { color: 'text-red-500', icon: AlertCircle, text: 'Error' };
      default:
        return { color: 'text-gray-500', icon: Clock, text: 'Unknown' };
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Video Sources</h1>
          <p className="text-gray-600 mt-1">
            Upload and manage video assets from Iconik for CDN distribution
          </p>
        </div>
      </div>

      {/* Upload Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Add Video Sources</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* CSV Upload */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <FileText className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-gray-900">CSV Upload</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Upload a CSV file with columns: village_id, iconik_url
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose CSV File
            </label>
          </div>

          {/* Manual Entry */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Plus className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-medium text-gray-900">Manual Entry</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Add individual video by Iconik URL and village selection
            </p>
            
            {!showManualInput ? (
              <Button
                variant="secondary"
                onClick={() => setShowManualInput(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Video
              </Button>
            ) : (
              <div className="space-y-3">
                <LocationFilter
                  value={manualLocation}
                  onChange={setManualLocation}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Iconik URL
                  </label>
                  <input
                    type="url"
                    value={manualIconikUrl}
                    onChange={(e) => setManualIconikUrl(e.target.value)}
                    placeholder="https://app.iconik.io/.../assets/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="primary"
                    onClick={handleManualAdd}
                    disabled={!manualLocation.village_id || !manualIconikUrl}
                  >
                    Add Video
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowManualInput(false);
                      setManualLocation({});
                      setManualIconikUrl('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Environment Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
          <div className="text-blue-800">
            <p className="font-medium">Development Environment</p>
            <p className="text-sm mt-1">
              Video upload to CDN is only available in production environment. In development, the upload API returns 404.
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {(error || validationError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <p className="text-red-700">{error || validationError}</p>
          </div>
        </div>
      )}

      {/* Videos List */}
      {videos.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Bulk Actions Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Videos ({videos.length})
                </h3>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Button variant="secondary" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="secondary" size="sm" onClick={clearSelection}>
                    Clear
                  </Button>
                  <span>
                    {selectedVideos.size} selected
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {selectedVideos.size > 0 && (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleBulkPreview}
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Preview Selected
                    </Button>
                    {(() => {
                      const selectedVideoItems = Array.from(selectedVideos).map(i => videos[i]);
                      const readyToUpload = selectedVideoItems.filter(video => 
                        video.status === 'ready' && video.metadata.title && video.metadata.program
                      ).length;
                      const needsEditing = selectedVideoItems.filter(video => 
                        video.status === 'ready' && (!video.metadata.title || !video.metadata.program)
                      ).length;
                      
                      return (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleBulkUpload}
                            disabled={loading || readyToUpload === 0}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Selected
                            {readyToUpload > 0 && <span className="ml-1">({readyToUpload})</span>}
                          </Button>
                          {needsEditing > 0 && (
                            <span className="text-xs text-amber-600">
                              {needsEditing} need metadata
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected
                    </Button>
                  </>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={clearAll}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>

          {/* Videos Grid */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {videos.map((video, index) => {
                const status = getStatusDisplay(video.status);
                const StatusIcon = status.icon;
                const isSelected = selectedVideos.has(index);
                
                return (
                  <div
                    key={index}
                    className={`border border-gray-200 rounded-lg overflow-hidden transition-all ${
                      isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:shadow-md'
                    }`}
                  >
                    {/* Video Thumbnail/Preview */}
                    <div className="aspect-video bg-gray-100 relative">
                      {video.preview?.url ? (
                        <div className="relative w-full h-full">
                          {playingVideo === index ? (
                            <video
                              src={video.preview.url}
                              controls
                              autoPlay
                              className="w-full h-full object-cover"
                              onPause={() => setPlayingVideo(null)}
                            />
                          ) : (
                            <>
                              <div className="w-full h-full bg-black flex items-center justify-center">
                                <VideoIcon className="h-12 w-12 text-white opacity-50" />
                              </div>
                              <button
                                onClick={() => handleVideoPlay(index)}
                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 hover:bg-opacity-40 transition-all"
                              >
                                <div className="bg-white bg-opacity-90 rounded-full p-3 hover:bg-opacity-100 transition-all">
                                  <Play className="h-6 w-6 text-gray-800" />
                                </div>
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <VideoIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">
                              {video.status === 'pending' ? 'Preview pending' : 'No preview'}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Selection checkbox */}
                      <div className="absolute top-2 left-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleVideoSelection(index)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                      </div>
                      
                      {/* Status badge */}
                      <div className="absolute top-2 right-2">
                        <div className={`flex items-center px-2 py-1 rounded text-xs font-medium bg-white bg-opacity-90 ${status.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.text}
                        </div>
                      </div>
                    </div>

                    {/* Video Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                            {video.metadata.title || video.preview?.filename || 'Untitled'}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {video.village_id}
                          </p>
                          {video.preview && (
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(video.preview.captured_at).toLocaleDateString()}
                            </p>
                          )}
                          {video.error && (
                            <p className="text-xs text-red-600 mt-1">{video.error}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-1 ml-2">
                          {video.status === 'ready' && (
                            <button
                              onClick={() => {
                                setEditingVideo({ index, video });
                                setEditForm({
                                  title: video.metadata.title || '',
                                  description: video.metadata.description || '',
                                  program: video.metadata.program || '',
                                  asset_type: video.metadata.asset_type || 'Video',
                                  event: video.metadata.event || ''
                                });
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600 rounded"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => removeVideo(index)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Metadata preview and validation */}
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1 mb-2 text-xs">
                          {video.metadata.program ? (
                            <span className="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {video.metadata.program}
                            </span>
                          ) : video.status === 'ready' && (
                            <span className="inline-flex items-center bg-red-100 text-red-800 px-2 py-1 rounded">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Program required
                            </span>
                          )}
                          {video.metadata.event && (
                            <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {video.metadata.event}
                            </span>
                          )}
                        </div>
                        
                        {video.status === 'ready' && (
                          <div className="text-xs">
                            {(!video.metadata.title || !video.metadata.program) ? (
                              <div className="flex items-center text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Missing required fields for upload
                              </div>
                            ) : (
                              <div className="flex items-center text-green-600 bg-green-50 px-2 py-1 rounded">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Ready to upload
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {videos.length === 0 && (
        <div className="text-center py-12">
          <VideoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Videos Added</h3>
          <p className="text-gray-600 mb-4">
            Upload a CSV file or add videos manually to get started
          </p>
        </div>
      )}

      {/* Metadata Edit Modal */}
      {editingVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">Edit Video Metadata</h2>
              <button
                onClick={() => setEditingVideo(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Video preview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <VideoIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{editingVideo.video.preview?.filename}</p>
                      <p className="text-sm text-gray-500">{editingVideo.video.village_id}</p>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter video title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter video description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Program */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Program *
                    </label>
                    <select
                      value={editForm.program}
                      onChange={(e) => setEditForm(prev => ({ ...prev, program: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Program</option>
                      {PROGRAM_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Asset Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Asset Type
                    </label>
                    <select
                      value={editForm.asset_type}
                      onChange={(e) => setEditForm(prev => ({ ...prev, asset_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {ASSET_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Event */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event
                  </label>
                  <select
                    value={editForm.event}
                    onChange={(e) => setEditForm(prev => ({ ...prev, event: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Event</option>
                    {EVENT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
              <Button
                variant="secondary"
                onClick={() => setEditingVideo(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  updateMetadata(editingVideo.index, editForm);
                  setEditingVideo(null);
                }}
                disabled={!editForm.title || !editForm.program}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};